<script setup>
import { computed, inject, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useNotificationContactsStore } from '@/stores/notificationContactsStore';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import { useI18n } from 'vue-i18n';
import { DialogConfirm, useSnackbarStore, PrimaryButton } from '@es-bill/es-bill-common-components';

const $log = inject('log');
const { t } = useI18n();
const router = useRouter();
const notificationContactsStore = useNotificationContactsStore();
const communitiesStore = useCommunitiesStore();
const snackbarStore = useSnackbarStore();

// Selected ECID for filtering
const selectedEcid = ref('');

// Prepare communities list with subtitle in props for autocomplete
const communitiesAvailable = computed(() =>
  communitiesStore.allCommunities.map(c => ({
    ...c,
    title: c.name,
    subtitle: c.ecid,
    value: c.ecid
  }))
);

// Custom filter function for community autocomplete
const communityFilter = (itemText, queryText, item) => {
  if (!queryText) return true;

  const query = queryText.toLowerCase();
  const name = item.raw.name?.toLowerCase() || '';
  const ecid = item.raw.ecid?.toLowerCase() || '';

  return name.includes(query) || ecid.includes(query);
};

// Build a map of ecid -> community name
const communityMap = computed(() => {
  const map = {};
  communitiesStore.allCommunities.forEach(c => { map[c.ecid] = c.name; });
  return map;
});

const tableHeader = computed(() => [
  { title: t('community'), value: 'communityName' },
  { title: t('name'), value: 'nameFriendly' },
  { title: t('salutation'), value: 'salutation' },
  { title: t('emailAddress'), value: 'emailAddress' },
  { title: t('phoneNumber'), value: 'phoneNumber' },
  { title: t('description'), value: 'description' },
  { title: t('actions'), value: 'actions', sortable: false },
]);

// Flattened and possibly filtered contacts with communityName
const contactsWithName = computed(() => {
  const base = notificationContactsStore.contacts || [];
  const filtered = selectedEcid.value
    ? base.filter(c => c.ecid === selectedEcid.value)
    : base;
  return filtered.map(c => ({
    ...c,
    communityName: communityMap.value[c.ecid] || c.ecid,
  }));
});

const isDialogVisible = ref(false);
const selectedContactId = ref(null);

function showDeleteDialog(index) {
  const record = contactsWithName.value[index];
  selectedContactId.value = record._id;
  isDialogVisible.value = true;
}

async function handleConfirm() {
  if (selectedContactId.value !== null) {
    const record = contactsWithName.value.find(c => c._id === selectedContactId.value);
    if (record) {
      try {
        await notificationContactsStore.deleteNotificationContact(record.ecid, selectedContactId.value);
        snackbarStore.showSnackbar(t('applicantMessages.deletedSuccess'), '', 'Success', 4000, []);
      } catch (error) {
        snackbarStore.showSnackbar(error?.response?.data?.message || t('unexpectedError'), '', 'Error', 10000, []);
      }
    }
    selectedContactId.value = null;
  }
  isDialogVisible.value = false;
}

function handleCancel() {
  selectedContactId.value = null;
  isDialogVisible.value = false;
}

function goToForm(index) {
  const record = contactsWithName.value[index];
  if (!record._id) {
    console.error('Contact missing _id field:', record);
    snackbarStore.showSnackbar(t('unexpectedError'), '', 'Error', 10000, []);
    return;
  }
  router.push({ name: 'notificationContactForm', params: { ecid: record.ecid, contactId: record._id } });
}

function goToAdd() {
  const params = {};
  if (selectedEcid.value) {
    params.ecid = selectedEcid.value;
  }
  router.push({ name: 'notificationContactForm', params });
}

onMounted(async () => {
  await notificationContactsStore.fetchNotificationContacts();
});
</script>

<template>
  <v-container class="pa-0">
    <p class="textHeadline">{{ t('notificationContacts') }}</p>
    <v-row align="center" class="mb-4">
      <v-col cols="6">
        <v-autocomplete
          bg-color="white"
          :items="communitiesAvailable"
          item-title="title"
          item-value="value"
          variant="outlined"
          v-model="selectedEcid"
          clearable
          creatable
          :placeholder="t('community')"
          :label="t('community')"
          :custom-filter="communityFilter"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props" :title="item.raw.title" :subtitle="item.raw.subtitle">
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="6" class="text-right">
        <PrimaryButton :text="t('add')" @click="goToAdd"/>
      </v-col>
    </v-row>
    <v-data-table
      :headers="tableHeader"
      :items="contactsWithName"
      class="mainViewContent elevation-2"
    >
      <!-- Community column with name and ECID subtitle -->
      <template v-slot:[`item.communityName`]="{ item }">
        <div class="d-flex flex-column">
          <span>{{ item.communityName }}</span>
          <small class="text--secondary">{{ item.ecid }}</small>
        </div>
      </template>
      <template v-slot:[`item.actions`]="{ index }">
        <v-icon @click="showDeleteDialog(index)" class="mr-2">mdi-delete</v-icon>
        <v-icon @click="goToForm(index)">mdi-pencil</v-icon>
      </template>
    </v-data-table>
    <DialogConfirm
      :modelValue="isDialogVisible"
      @update:modelValue="isDialogVisible = $event"
      @confirm="handleConfirm"
      @cancel="handleCancel"
      :title="t('areYouSure')"
      :message="t('deleteConfirmationGeneric')"
      :confirmText="t('confirm')"
      :cancelText="t('cancel')"
    />
  </v-container>
</template>

<style scoped>
</style>
